import React, { useState, useMemo } from 'react';
import axios from 'axios';

const ORGA_URL = process.env.REACT_APP_ORGANIZER_API_URL || 'http://localhost:5001';
const DEFAULT_RULES = { pointsPerSet: 21, setsToWin: 1, winBy: 2 };

// Une équipe gagne le set si elle atteint la cible avec l'écart requis, ou le plafond (sans besoin d'écart).
function sideWins(cur, other, r) {
  if (r.capOn && r.cap && cur >= r.cap && cur > other) return true;
  if (r.winBy === 1) return cur >= r.pointsPerSet && cur > other; // "sec" : premier arrivé, pas de deuce
  return cur >= r.pointsPerSet && (cur - other) >= (r.winBy || 2);
}

// Recalcule l'état du match à partir de l'historique des points (permet un undo trivial).
function computeState(history, r) {
  const sets = [];
  let curA = 0, curB = 0, winner = null;
  for (const p of history) {
    if (winner) break;
    if (p === 'a') curA++; else curB++;
    if (sideWins(curA, curB, r) || sideWins(curB, curA, r)) { sets.push({ a: curA, b: curB }); curA = 0; curB = 0; }
    const aSets = sets.filter(s => s.a > s.b).length;
    const bSets = sets.filter(s => s.b > s.a).length;
    if (aSets >= r.setsToWin) winner = 'a';
    else if (bSets >= r.setsToWin) winner = 'b';
  }
  return { sets, curA, curB, winner };
}
const wonCount = (sets, side) => sets.filter(s => (side === 'a' ? s.a > s.b : s.b > s.a)).length;

export default function MatchScorer({ match, tournamentId, format, onClose, onSubmitted }) {
  const rules = useMemo(() => ({ ...DEFAULT_RULES, ...((match.round === 'poule' ? format?.pool : format?.finale) || {}) }), [format, match.round]);
  const [history, setHistory] = useState([]);
  const [swapped, setSwapped] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { sets, curA, curB, winner } = useMemo(() => computeState(history, rules), [history, rules]);

  const addPoint = side => !winner && setHistory(h => [...h, side]);
  const targetLabel = `${rules.pointsPerSet} pts · ${rules.winBy === 1 ? 'sec' : `${rules.winBy} d'écart`}${rules.capOn && rules.cap ? ` (plafond ${rules.cap})` : ''}`;

  const handleClose = () => {
    if (history.length && !winner && !window.confirm('Le match est en cours — fermer sans enregistrer le score ?')) return;
    onClose();
  };

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${ORGA_URL}/api/tournaments/${tournamentId}/matches/${match.id}/score`, { sets },
        { headers: { Authorization: `Bearer ${token}` } });
      onSubmitted && onSubmitted();
      onClose();
    } catch {
      setError('Échec de l’envoi — vérifie ta connexion.');
    } finally {
      setSubmitting(false);
    }
  };

  const left = swapped ? 'b' : 'a';
  const right = swapped ? 'a' : 'b';
  const nameOf = side => (side === 'a' ? match.teamA : match.teamB);
  const curOf = side => (side === 'a' ? curA : curB);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'var(--ink)', display: 'flex', flexDirection: 'column' }}>
      {/* En-tête */}
      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--coral)' }}>
            {match.isPlaying ? 'Ton match' : 'Tu arbitres'}
          </span>
          <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>{targetLabel}</span>
        </div>
        <button onClick={handleClose} aria-label="Fermer" style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--ink-3)', color: 'var(--text)', fontSize: 18, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>×</button>
      </div>

      {winner ? (
        <Summary match={match} sets={sets} winner={winner} onSubmit={submit} submitting={submitting} error={error} />
      ) : (
        <>
          {/* Indicateurs de sets gagnés */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24, padding: '10px 0' }}>
            {['a', 'b'].map(s => (
              <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-3)', maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(s)}</span>
                <span className="display" style={{ fontSize: 20, color: 'var(--sun-2)' }}>{wonCount(sets, s)}</span>
              </div>
            ))}
          </div>

          {/* Zones tappables */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px' }}>
            {[left, right].map(s => (
              <button key={s} onClick={() => addPoint(s)} className="card"
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, border: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{nameOf(s)}</span>
                <span className="display" style={{ fontSize: 84, lineHeight: 1, color: 'var(--text)' }}>{curOf(s)}</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Toucher pour +1 point</span>
              </button>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, padding: 16, flexShrink: 0 }}>
            <button onClick={() => setHistory(h => h.slice(0, -1))} disabled={!history.length} style={actionBtn}>↩ Annuler</button>
            <button onClick={() => setSwapped(sw => !sw)} style={actionBtn}>⇄ Changer de côté</button>
          </div>
        </>
      )}
    </div>);
}

function Summary({ match, sets, winner, onSubmit, submitting, error }) {
  const winnerName = winner === 'a' ? match.teamA : match.teamB;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px 20px', overflowY: 'auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <span aria-hidden="true" style={{ fontSize: 40 }}>🏆</span>
        <h2 style={{ margin: '8px 0 0', fontSize: 20, fontWeight: 800 }}>{winnerName} gagne</h2>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'flex', padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <span style={{ flex: 1, fontWeight: 700, fontSize: 13 }}>{match.teamA}</span>
          <span style={{ flex: 1, fontWeight: 700, fontSize: 13, textAlign: 'right' }}>{match.teamB}</span>
        </div>
        {sets.map((s, i) => (
          <div key={i} style={{ display: 'flex', padding: '10px 14px', borderBottom: i < sets.length - 1 ? '1px solid var(--line)' : 'none' }}>
            <span className="mono" style={{ flex: 1, fontWeight: 700, color: s.a > s.b ? 'var(--lime)' : 'var(--text)' }}>{s.a}</span>
            <span className="mono" style={{ flex: 1, fontWeight: 700, textAlign: 'right', color: s.b > s.a ? 'var(--lime)' : 'var(--text)' }}>{s.b}</span>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center', margin: '0 0 16px' }}>À valider par les joueurs et l'arbitre.</p>
      {error && <p style={{ fontSize: 12.5, color: 'var(--coral)', textAlign: 'center', margin: '0 0 10px' }}>{error}</p>}
      <button onClick={onSubmit} disabled={submitting}
        style={{ width: '100%', padding: 14, borderRadius: 'var(--r-md)', border: 'none', background: 'var(--sun)', color: '#1A1206', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
        {submitting ? 'Envoi…' : error ? 'Réessayer' : 'Envoyer le score'}
      </button>
    </div>);
}

const actionBtn = { flex: 1, padding: 13, borderRadius: 'var(--r-md)', border: '1px solid var(--line)', background: 'var(--ink-3)', color: 'var(--text)', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' };
